import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image
} from 'react-bootstrap';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const AnnouncementsManager = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  
  // Options for icons and colors
  const [availableIcons, setAvailableIcons] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    attachment: '',
    icon: 'fas fa-bullhorn',
    icon_color: 'danger',
    date: new Date().toISOString().split('T')[0], // Today's date
    is_active: true
  });

  const API_BASE_URL = BASE_URL;
  
  useEffect(() => {
    // Fetch user data from localStorage or API
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    
    fetchAnnouncements();
    fetchOptions();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      attachment: '',
      icon: 'fas fa-bullhorn',
      icon_color: 'danger',
      date: new Date().toISOString().split('T')[0],
      is_active: true
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/announcements.php?admin=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const processedAnnouncements = response.data.data.map(announcement => ({
          ...announcement,
          id: parseInt(announcement.id),
          is_active: announcement.is_active === "1" || announcement.is_active === 1
        }));
        
        setAnnouncements(processedAnnouncements);
      } else {
        setError('Failed to load announcements');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      setOptionsLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/announcements.php?options=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setAvailableIcons(response.data.data.icons || []);
        setAvailableColors(response.data.data.colors || {});
      }
    } catch (err) {
      console.error('Error fetching options:', err);
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        attachment_file: file
      }));
    }
  };

  const removeAttachment = () => {
    setFormData(prev => ({
      ...prev,
      attachment: '',
      attachment_file: null
    }));
  };

  const uploadAttachment = async (file) => {
    try {
      setUploadingAttachment(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('attachment', file);
      
      const response = await axios.post(`${API_BASE_URL}/upload_attachment.php`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        return response.data.attachment_url;
      }
      return null;
    } catch (err) {
      console.error('Attachment upload error:', err);
      return null;
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let dataToSend = { ...formData };
      
      // Remove attachment_file from dataToSend if it exists
      if (dataToSend.attachment_file) {
        delete dataToSend.attachment_file;
      }
      
      // If there's a new attachment to upload
      if (formData.attachment_file) {
        const uploadedUrl = await uploadAttachment(formData.attachment_file);
        if (uploadedUrl) {
          dataToSend.attachment = uploadedUrl;
        } else {
          setError('Failed to upload attachment');
          return;
        }
      }
      
      const token = localStorage.getItem('token');
      let response;
      
      if (editingAnnouncement) {
        response = await axios.put(
          `${API_BASE_URL}/announcements.php?id=${editingAnnouncement.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/announcements.php`, dataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      if (response.data.success) {
        setSuccess(editingAnnouncement ? 'Announcement updated!' : 'Announcement added!');
        setShowModal(false);
        resetForm();
        setEditingAnnouncement(null);
        fetchAnnouncements();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (response.data.message || ''));
      }
    } catch (err) {
      setError('Error saving announcement: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this announcement?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_BASE_URL}/announcements.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setSuccess('Announcement deleted!');
          fetchAnnouncements();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete announcement: ' + (response.data.message || ''));
        }
      } catch (err) {
        setError('Error deleting announcement');
        console.error(err);
      }
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content || '',
      attachment: announcement.attachment || '',
      icon: announcement.icon || 'fas fa-bullhorn',
      icon_color: announcement.icon_color || 'danger',
      date: announcement.date,
      is_active: announcement.is_active === true || announcement.is_active === "1" || announcement.is_active === 1
    });
    
    setShowModal(true);
  };

  const handleToggleActive = async (announcement) => {
    try {
      const currentActive = announcement.is_active === true || announcement.is_active === "1" || announcement.is_active === 1;
      const newActiveStatus = !currentActive;
      
      const updatedData = {
        title: announcement.title,
        content: announcement.content || '',
        attachment: announcement.attachment || '',
        icon: announcement.icon || 'fas fa-bullhorn',
        icon_color: announcement.icon_color || 'danger',
        date: announcement.date,
        is_active: newActiveStatus ? 1 : 0
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/announcements.php?id=${announcement.id}`,
        updatedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        fetchAnnouncements();
      } else {
        setError('Failed to update status: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError('Failed to update status');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAnnouncement(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'No content';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getFileIcon = (filename) => {
    if (!filename) return 'fas fa-file';
    const ext = filename.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf': return 'fas fa-file-pdf';
      case 'doc':
      case 'docx': return 'fas fa-file-word';
      case 'txt': return 'fas fa-file-alt';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return 'fas fa-file-image';
      default: return 'fas fa-file';
    }
  };

  if (loading) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading announcements...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Announcements Manager</h2>
            <p className="text-muted">Manage your website announcements</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => {
              setShowModal(true);
            }}>
              <i className="fas fa-plus me-2"></i>Add Announcement
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th width="60">Icon</th>
                  <th>Title</th>
                  <th>Content Preview</th>
                  <th>Date</th>
                  <th>Attachment</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th width="150">Actions</th>
                </tr>
              </thead>
              <tbody>
                {announcements.map((announcement) => (
                  <tr key={announcement.id}>
                    <td>
                      <div className={`text-${announcement.icon_color}`}>
                        <i className={`${announcement.icon} fa-lg`}></i>
                      </div>
                    </td>
                    <td>
                      <strong>{announcement.title}</strong>
                    </td>
                    <td>
                      <small className="text-muted">
                        {truncateText(announcement.content, 60)}
                      </small>
                    </td>
                    <td>
                      {new Date(announcement.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td>
                      {announcement.attachment ? (
                        <a 
                          href={`${UPLOAD_BASE_URL}${announcement.attachment}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          <i className={`${getFileIcon(announcement.attachment)} me-2`}></i>
                          <small>View File</small>
                        </a>
                      ) : (
                        <span className="text-muted">No attachment</span>
                      )}
                    </td>
                    <td>
                      <Form.Check
                        type="switch"
                        checked={announcement.is_active === true || announcement.is_active === "1" || announcement.is_active === 1}
                        onChange={() => handleToggleActive(announcement)}
                        label={(announcement.is_active === true || announcement.is_active === "1" || announcement.is_active === 1) ? 'Active' : 'Inactive'}
                        key={`active-${announcement.id}-${announcement.is_active}`}
                      />
                    </td>
                    <td>
                      <small className="text-muted">
                        {formatDate(announcement.created_at)}
                      </small>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-2"
                        onClick={() => handleEdit(announcement)}
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(announcement.id)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {announcements.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-bullhorn fa-3x mb-3"></i>
                <p>No announcements found. Add your first announcement!</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Add/Edit Announcement Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingAnnouncement ? 'Edit Announcement' : 'Add Announcement'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Title *</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter announcement title"
                    />
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date *</Form.Label>
                    <Form.Control
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Content *</Form.Label>
                <Form.Control
                  as="textarea"
                  name="content"
                  value={formData.content}
                  onChange={handleTextareaChange}
                  rows={6}
                  required
                  placeholder="Enter announcement content..."
                />
              </Form.Group>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Icon</Form.Label>
                    {optionsLoading ? (
                      <Spinner size="sm" animation="border" />
                    ) : (
                      <Form.Select
                        name="icon"
                        value={formData.icon}
                        onChange={handleInputChange}
                      >
                        {availableIcons.map((icon, index) => (
                          <option key={index} value={icon}>
                            <i className={`${icon} me-2`}></i>
                            {icon.replace('fas fa-', '').replace('-', ' ')}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Icon Color</Form.Label>
                    {optionsLoading ? (
                      <Spinner size="sm" animation="border" />
                    ) : (
                      <Form.Select
                        name="icon_color"
                        value={formData.icon_color}
                        onChange={handleInputChange}
                      >
                        {Object.entries(availableColors).map(([value, label]) => (
                          <option key={value} value={value}>
                            <Badge bg={value}>{label}</Badge>
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Attachment</Form.Label>
                    <Form.Control
                      type="file"
                      onChange={handleFileChange}
                      disabled={uploadingAttachment}
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                    />
                    <Form.Text className="text-muted">
                      Upload PDF, DOC, TXT, or image files (optional)
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <div>
                      <Form.Check
                        type="switch"
                        name="is_active"
                        label="Active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Inactive announcements won't be shown
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Current attachment preview */}
              {formData.attachment && (
                <div className="mb-3">
                  <Form.Label>Current Attachment</Form.Label>
                  <div className="d-flex align-items-center">
                    <Badge bg="secondary" className="me-2">
                      <i className={`${getFileIcon(formData.attachment)} me-1`}></i>
                      Attached
                    </Badge>
                    
                    <a 
                      href={`${UPLOAD_BASE_URL}${formData.attachment}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="me-2"
                    >
                      <small>View</small>
                    </a>
                    
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={removeAttachment}
                    >
                      <i className="fas fa-trash me-1"></i> Remove
                    </Button>
                  </div>
                </div>
              )}
              
              {editingAnnouncement && (
                <div className="alert alert-secondary">
                  <small>
                    <i className="fas fa-info-circle me-2"></i>
                    Created: {formatDate(editingAnnouncement.created_at)}
                  </small>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploadingAttachment}>
                {uploadingAttachment && <Spinner animation="border" size="sm" className="me-2" />}
                {editingAnnouncement ? 'Update' : 'Add'} Announcement
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default AnnouncementsManager;